import React from 'react';
import { Box, Typography, IconButton, Alert } from '@mui/material';
import { Button } from 'components/index';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Cell } from 'material-react-table';
import { SectionFieldDTO } from '../magic-report.type';
import { ProjectLineItemDTO } from '../../../project/project.type';
import { formatCurrency, parseCurrency } from '../../../../common/utils/common.util';
import { usePrompt } from '../../../../components/AppPrompt';

export interface ReallocationRow {
  lineItemId: string;
  description: string;
  originalBudget: number;
  totalReallocation: number;
  percentChange: number;
  revisedBudget: number;
  currentReallocation: number;
  isLastMonth?: boolean;
  /**
   * Which covered draw (G702) this row belongs to. All draws' rows share the single
   * `ReallocationsRows` field — the backend's reset transform matches that exact field name to
   * fold rows into the historical baseline — so the draw is recorded per row instead of per field.
   * Absent on rows saved before multi-draw; those bind to the first covered draw on load.
   */
  drawId?: string;
  // Snapshot of totalReallocation as of the last budget sync - distinct from totalReallocation
  // itself, which is directly editable in the table. Reconciliation must diff against this
  // baseline, not the (possibly manually-edited) totalReallocation value.
  lastSyncedTotalReallocation?: number;
}

const BUDGET_TOTAL_TOLERANCE = 0.01;

const parseRow = (value?: string): ReallocationRow | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed as ReallocationRow;
  } catch {
    return null;
  }
};

const serializeRow = (row: ReallocationRow): string => JSON.stringify(row);

const ensureRowDefaults = (row: Partial<ReallocationRow>): ReallocationRow => {
  const originalBudget = row.originalBudget ?? 0;
  const totalReallocation = row.totalReallocation ?? 0;
  const currentReallocation = row.currentReallocation ?? totalReallocation;
  // Revised Budget reflects the line item's actual current state (original + all reallocations
  // ever), not just this period's delta - otherwise it would look unchanged for line items whose
  // reallocation happened in an earlier period.
  const revisedBudget = originalBudget + totalReallocation;
  const percentChange = originalBudget !== 0 ? (currentReallocation / originalBudget) * 100 : 0;

  return {
    lineItemId: row.lineItemId || '',
    description: row.description || '',
    originalBudget,
    totalReallocation,
    percentChange,
    revisedBudget,
    currentReallocation,
    isLastMonth: row.isLastMonth ?? false,
    // Preserved so a row never loses which draw it belongs to when normalized.
    drawId: row.drawId,
    lastSyncedTotalReallocation: row.lastSyncedTotalReallocation ?? totalReallocation,
  };
};

interface ReallocationsTableProps {
  rowsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  lineItems: ProjectLineItemDTO[] | undefined;
  onRowsSynced?: (rows: ReallocationRow[]) => void;
  onSyncFunctionReady?: (syncFn: () => Promise<void>) => void;
}

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const ReallocationsTable: React.FC<ReallocationsTableProps> = ({
  rowsField,
  handleFieldChange,
  lineItems,
  onRowsSynced,
  onSyncFunctionReady,
}) => {
  const { getConfirmation } = usePrompt();
  const currentRows: ReallocationRow[] =
    rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ReallocationRow => r !== null) || [];

  // A row is only "this period's" reallocation if its currentReallocation is nonzero.
  // totalReallocation (the cumulative amount from the budget) can be nonzero purely because
  // of reallocations from a prior period folded into the baseline by the backend on report
  // reset - that alone must not surface the row as a current change.
  const buildRowsFromLineItems = (): ReallocationRow[] => {
    const activeLineItems = (lineItems || []).filter(li => !li.isDeleted);

    return activeLineItems
      .map(li => {
        // Only reconcile against an existing CURRENT-period row. isLastMonth rows are the
        // folded-in baseline from a prior period and are appended separately by the caller -
        // treating one as "existing" here would incorrectly carry isLastMonth: true forward
        // onto what should become a fresh current-period row.
        const existing = currentRows.find(r => r.lineItemId === li.id && !r.isLastMonth);
        // The most recent known baseline for this line item, whether from a current-period
        // row or (if none exists yet) a carried-over last-month row.
        const baseline = existing ?? currentRows.find(r => r.lineItemId === li.id && r.isLastMonth);
        const originalBudget = li.originalBudget || 0;
        const totalReallocation = li.reallocation || 0;

        if (baseline) {
          // currentReallocation represents the delta since the last time we synced this row
          // from the budget. The last-synced baseline is tracked separately from totalReallocation
          // itself (which is directly editable in the table) so a manual edit to that cell can't
          // masquerade as a budget change on the next sync. If the budget's cumulative total hasn't
          // moved (within tolerance) since the last sync, whatever is stored on the current-period
          // row is either a manual edit (preserve it) or, if there is no current-period row yet,
          // there is no change this period (stays 0). If the budget total HAS moved, the difference
          // against the last-synced total is the real delta.
          const lastSyncedTotal = baseline.lastSyncedTotalReallocation ?? baseline.totalReallocation;
          const budgetMoved = Math.abs(totalReallocation - lastSyncedTotal) > BUDGET_TOTAL_TOLERANCE;
          const priorCurrentReallocation = existing?.currentReallocation ?? 0;
          const newCurrentReallocation = budgetMoved
            ? priorCurrentReallocation + (totalReallocation - lastSyncedTotal)
            : priorCurrentReallocation;

          return ensureRowDefaults({
            lineItemId: li.id,
            description: li.lineItem?.description || baseline.description,
            originalBudget,
            totalReallocation,
            currentReallocation: newCurrentReallocation,
            isLastMonth: false,
            lastSyncedTotalReallocation: totalReallocation,
          });
        }

        // Brand new reallocated line item: the entire cumulative amount is new this period.
        if (totalReallocation === 0) return null;

        return ensureRowDefaults({
          lineItemId: li.id,
          description: li.lineItem?.description || '',
          originalBudget,
          totalReallocation,
          currentReallocation: totalReallocation,
        });
      })
      .filter((r): r is ReallocationRow => r !== null && r.currentReallocation !== 0);
  };

  const syncRowsFromBudget = async (skipConfirmation = false) => {
    if (!skipConfirmation) {
      const isConfirmed = await getConfirmation({
        heading: 'Refresh Reallocations',
        description: 'This will refresh the reallocation table with the latest data from the budget. Last month entries will be retained.',
        confirmButton: {
          label: 'Continue',
          color: 'primary',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return;
    }

    // Get new rows from line items
    const newRows = buildRowsFromLineItems();

    // Preserve last month rows that don't overlap with new rows
    const lastMonthRows = currentRows.filter(row => row.isLastMonth === true);
    const newRowIds = new Set(newRows.map(r => r.lineItemId));
    const filteredLastMonthRows = lastMonthRows.filter(row => !newRowIds.has(row.lineItemId));

    // Combine: new rows at top, last month rows at bottom
    const combinedRows = [...newRows, ...filteredLastMonthRows];

    const entries =
      combinedRows.length === 0
        ? []
        : combinedRows.map((row, index) => ({
            order: index,
            value: serializeRow(row),
          }));

    handleFieldChange('ReallocationsRows', {
      entries,
    });

    onRowsSynced?.(combinedRows);
  };

  // Note: Auto-sync removed - user must manually click "Get Reallocations" button
  // This prevents overwriting saved data without confirmation.
  // However, when there are *no* saved rows yet, we still want the
  // budget-derived rows to be persisted so Save Draft sends them.

  const rowsToRender = currentRows.length ? currentRows : buildRowsFromLineItems();

  // A row is only shown when it has a nonzero Current Reallocation this period. Rows folded
  // into the historical baseline (isLastMonth, or any row whose delta is now 0) still live in
  // the saved data - for mismatch checks, exports, cover-page stats - but have nothing new to
  // report, so they don't clutter this table.
  const visibleRows = React.useMemo(() => rowsToRender.filter(row => row.currentReallocation !== 0), [rowsToRender]);

  // When there are no saved rows but we have budget-derived rows,
  // persist them into the ReallocationsRows field so they are saved.
  React.useEffect(() => {
    const hasSavedRows = (rowsField?.entries?.length || 0) > 0;
    if (hasSavedRows) return;

    if (!rowsToRender.length) return;

    const entries =
      rowsToRender.length === 0
        ? []
        : rowsToRender.map((row, index) => ({
            order: index,
            value: serializeRow(row),
          }));

    handleFieldChange('ReallocationsRows', { entries });
    onRowsSynced?.(rowsToRender);
  }, [rowsField, rowsToRender, handleFieldChange, onRowsSynced]);

  // Expose sync function to parent
  React.useEffect(() => {
    if (onSyncFunctionReady) {
      onSyncFunctionReady(() => syncRowsFromBudget(true));
    }
  }, [onSyncFunctionReady]);

  const handleSaveCell = React.useCallback(
    async (cell: MRT_Cell<ReallocationRow>, value: any) => {
      const row = cell.row.original;
      const field = cell.column.id as 'originalBudget' | 'totalReallocation' | 'currentReallocation';

      const numericValue = typeof value === 'string' ? parseCurrency(value) : parseFloat(value) || 0;

      // Zeroing out Current Reallocation removes the row from the table entirely (it's only
      // shown while nonzero), so confirm with the user before applying that edit.
      if (field === 'currentReallocation' && numericValue === 0 && row.currentReallocation !== 0) {
        await new Promise(resolve => setTimeout(resolve, 0));

        const isConfirmed = await getConfirmation({
          heading: 'Remove Reallocation',
          description:
            'Setting Current Reallocation to 0 will remove this line item from the table. Use "Get Reallocations" to bring it back.',
          confirmButton: {
            label: 'Continue',
            color: 'primary',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });

        if (!isConfirmed) return;
      }

      // Recalculate current rows from field to ensure we have latest data
      const savedRows: ReallocationRow[] =
        rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ReallocationRow => r !== null) || [];
      // If nothing saved yet, fall back to the currently rendered rows so first edit persists
      const latestRows: ReallocationRow[] = savedRows.length ? savedRows : rowsToRender;

      const updatedRow = { ...row, [field]: numericValue } as ReallocationRow;
      const recalculatedRow = ensureRowDefaults(updatedRow);

      const updatedRows = latestRows.map(r => (r.lineItemId === row.lineItemId ? recalculatedRow : r));
      const entries = updatedRows.map((r, index) => ({
        order: index,
        value: serializeRow(r),
      }));
      handleFieldChange('ReallocationsRows', { entries });
      onRowsSynced?.(updatedRows);
    },
    [rowsField, handleFieldChange, onRowsSynced, rowsToRender, getConfirmation],
  );

  // Calculate totals from the visible (current-period) rows only
  const totalsRow: ReallocationRow = React.useMemo(
    () => ({
      lineItemId: 'totals',
      description: 'Total Change Amounts:',
      originalBudget: visibleRows.reduce((sum, row) => sum + row.originalBudget, 0),
      totalReallocation: visibleRows.reduce((sum, row) => sum + row.totalReallocation, 0),
      currentReallocation: visibleRows.reduce((sum, row) => sum + row.currentReallocation, 0),
      revisedBudget: visibleRows.reduce((sum, row) => sum + row.revisedBudget, 0),
      percentChange: 0, // Will be calculated
    }),
    [visibleRows],
  );

  const totalPercentChange = totalsRow.originalBudget !== 0 ? (totalsRow.currentReallocation / totalsRow.originalBudget) * 100 : 0;
  totalsRow.percentChange = totalPercentChange;

  // Calculate total reallocation from Step 2 (lineItems)
  const totalReallocationFromBudget = React.useMemo(() => {
    if (!lineItems) return 0;
    return lineItems.filter(li => !li.isDeleted && (li.reallocation || 0) !== 0).reduce((sum, li) => sum + (li.reallocation || 0), 0);
  }, [lineItems]);

  // Check if saved total reallocation matches budget total. Last month rows still hold their
  // line item's totalReallocation (only currentReallocation gets folded to 0 on reset), so they
  // must be included here too - otherwise this would false-positive for every line item whose
  // reallocation happened in a prior period and is no longer shown as a current-period row.
  const hasMismatch = React.useMemo(() => {
    if (currentRows.length === 0) return false;
    const savedTotal = currentRows.reduce((sum, row) => sum + row.totalReallocation, 0);
    return Math.abs(savedTotal - totalReallocationFromBudget) > 0.01;
  }, [currentRows, totalReallocationFromBudget]);

  const rightAlignedProps = {
    muiTableBodyCellProps: {
      align: 'right' as const,
      sx: {
        fontSize: '0.875rem',
        border: 'none',
      },
    },
    muiTableHeadCellProps: {
      align: 'right' as const,
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        position: 'sticky',
        top: 0,
        color: '#ffffff',
        padding: '8px 9px',
        border: 'none',
        justifyContent: 'center',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '& .Mui-TableHeadCell-Content-Wrapper': {
          justifyContent: 'flex-end',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      }),
    },
    muiTableFooterCellProps: {
      align: 'right' as const,
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        padding: '8px 9px',
        border: 'none',
      }),
    },
  };

  const columns = React.useMemo<MRT_ColumnDef<ReallocationRow>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Reallocated Line Item',
        size: 200,
        enableEditing: false,
        Footer: () => 'Total Change Amounts:',
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            position: 'sticky',
            top: 0,
            color: '#ffffff',
            padding: '8px 9px',
            border: 'none',
            justifyContent: 'center',
            fontSize: '0.9rem',
            fontWeight: 'normal',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            '&[data-pinned="true"]:not([role="columnheader"])::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]:not([role="columnheader"])': {
              zIndex: 1,
            },
          }),
        },
        muiTableBodyCellProps: ({ row }) => {
          const bg = row.original.isLastMonth ? '#e0e0e0' : row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
          return {
            sx: {
              backgroundColor: bg,
              border: 'none',
              fontSize: '0.875rem',
              '&[data-pinned="true"]::before': {
                backgroundColor: bg,
              },
            },
          };
        },
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            fontWeight: 'normal',
            fontSize: '0.9rem',
            padding: '8px 9px',
            border: 'none',
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
          }),
        },
      },
      {
        accessorKey: 'originalBudget',
        header: 'Original Budget',
        size: 120,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(totalsRow.originalBudget.toString(), true),
      },
      {
        accessorKey: 'totalReallocation',
        header: 'Total Reallocations',
        size: 135,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(totalsRow.totalReallocation.toString(), true),
      },
      {
        accessorKey: 'percentChange',
        header: '% Change',
        size: 90,
        Cell: ({ cell }) => formatPercent(cell.getValue<number>() ?? 0),
        enableEditing: false,
        ...rightAlignedProps,
        Footer: () => formatPercent(totalPercentChange),
      },
      {
        accessorKey: 'revisedBudget',
        header: 'Revised Budget from Reallocations',
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        enableEditing: false,
        ...rightAlignedProps,
        Footer: () => formatCurrency(totalsRow.revisedBudget.toString(), true),
      },
      {
        accessorKey: 'currentReallocation',
        header: 'Current Reallocation',
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(totalsRow.currentReallocation.toString(), true),
      },
    ],
    [totalsRow, totalPercentChange, handleSaveCell],
  );

  const table = useMaterialReactTable({
    columns,
    data: visibleRows,
    initialState: {
      density: 'compact',
      showGlobalFilter: false,
      columnPinning: { left: ['description'] },
    },
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableSorting: false,
    enableStickyFooter: true,
    editDisplayMode: 'cell',
    enableEditing: true,
    getRowId: row => row.lineItemId,
    muiTableProps: {
      sx: {
        marginTop: '0px',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        height: '100%',
        maxHeight: '600px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'auto',
        // Override MRT's pinned cell alignment to center
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '8px 9px',
        border: 'none',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '& .MuiBox-root': {
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
      }),
    },
    muiTableFooterCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        padding: '8px 9px',
        border: 'none',
        '&[data-pinned="true"]::before': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]': {
          zIndex: 1,
        },
      }),
    },
    muiTableBodyRowProps: ({ row }) => {
      const backgroundColor = row.original.isLastMonth ? '#e0e0e0' : row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
      return {
        sx: {
          backgroundColor: backgroundColor + ' !important',
          border: 'none',
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
        },
      };
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        fontSize: '0.875rem',
      },
    },
  });

  if (!visibleRows.length) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant='body2' color='text.secondary'>
          No line items with a reallocation this period were found in the Review Budget step.
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Button variant='outlined' size='small' onClick={syncRowsFromBudget}>
            Get Reallocations
          </Button>
        </Box>
      </Box>
    );
  }

  const addToActionItems = rowsField?.addToActionItems || false;

  const handleBookmarkToggle = () => {
    handleFieldChange('ReallocationsRows', { addToActionItems: !addToActionItems });
  };

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {hasMismatch && (
        <Alert severity='warning' sx={{ mb: 1, fontSize: '0.875rem' }}>
          Warning: The total reallocation in the table below ({formatCurrency(totalsRow.totalReallocation.toString(), true)}) does not match
          the total from current budget ({formatCurrency(totalReallocationFromBudget.toString(), true)}).
          <br /> Click &quot;Get Reallocations&quot; to refresh the data.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        <Typography variant='body2' color='text.secondary'>
          The Contractor has made the following reallocations since our last report:
        </Typography>
        <Button variant='outlined' size='small' onClick={syncRowsFromBudget}>
          Get Reallocations
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, minWidth: 0, overflow: 'auto' }}>
          <MaterialReactTable table={table} />
        </Box>
        <IconButton size='small' onClick={handleBookmarkToggle} sx={{ mt: 0.5, flexShrink: 0 }}>
          {addToActionItems ? (
            <BookmarkIcon color='primary' sx={{ fontSize: 20 }} />
          ) : (
            <BookmarkBorderIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};
